import { Mail, MapPin, MessageCircle, Phone } from "lucide-react";
import { FaqList } from "@/components/site/FaqList";
import { LeadForm } from "@/components/kit/LeadForm";
import { Reveal } from "@/components/site/Motion";
import { useList, Faq } from "@/lib/api";
import { FALLBACK_FAQS, SITE, waLink } from "@/lib/content";

export function useFaqs(featured = false) {
  return useList<Faq>(featured ? "/faqs?featured=true" : "/faqs", FALLBACK_FAQS).items;
}

export function WhatsAppCard() {
  return (
    <a href={waLink()} target="_blank" rel="noreferrer" data-testid="whatsapp-card" className="group flex items-center gap-5 rounded-[24px] bg-forest p-6 ring-1 ring-cream/10 transition-colors duration-500 hover:bg-pine">
      <span className="grid h-14 w-14 shrink-0 place-items-center rounded-full bg-leaf text-forest-deep transition-transform duration-500 group-hover:scale-110"><MessageCircle className="h-6 w-6" /></span>
      <span>
        <span className="block font-display text-2xl text-cream">Still have questions?</span>
        <span className="block text-sm text-cream/80">Chat with Sony on WhatsApp</span>
      </span>
    </a>
  );
}

export function InfoStrip() {
  const items = [
    { Icon: Phone, title: SITE.phoneDisplay, sub: SITE.hours, href: `tel:${SITE.phoneTel}`, id: "phone" },
    { Icon: Mail, title: SITE.email, sub: "We reply within a working day", href: `mailto:${SITE.email}`, id: "email" },
    { Icon: MapPin, title: SITE.city, sub: SITE.online, id: "location" },
  ];
  return (
    <div className="grid gap-px overflow-hidden rounded-[24px] bg-cream/10 sm:grid-cols-3" data-testid="contact-info-strip">
      {items.map(({ Icon, title, sub, href, id }) => {
        const body = (
          <>
            <span className="icon-ring h-11 w-11"><Icon className="h-4 w-4" /></span>
            <span className="min-w-0"><span className="block break-all text-sm font-semibold text-cream">{title}</span><span className="block text-xs text-cream/75">{sub}</span></span>
          </>
        );
        return href ? (
          <a key={id} href={href} data-testid={`contact-${id}-link`} className="flex items-center gap-4 bg-forest-deep p-5 transition-colors duration-300 hover:bg-forest">{body}</a>
        ) : (
          <div key={id} data-testid={`contact-${id}`} className="flex items-center gap-4 bg-forest-deep p-5">{body}</div>
        );
      })}
    </div>
  );
}

export function FaqContact({ idPrefix = "contact", showMap = false }: { idPrefix?: string; showMap?: boolean }) {
  const faqs = useFaqs();
  return (
    <section data-testid="faq-contact-section" className="bg-forest-deep pb-24 pt-4 text-cream sm:pb-32">
      <div className="wrap grid gap-10 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <Reveal>
            <p className="eyebrow-dark">Frequently asked questions</p>
            <div className="mt-6"><FaqList items={faqs} testPrefix="faq-page" dark /></div>
          </Reveal>
          <Reveal className="mt-8"><WhatsAppCard /></Reveal>
        </div>
        <Reveal delay={0.1} className="lg:col-span-5">
          <div id="book" className="lg:sticky lg:top-28"><LeadForm idPrefix={idPrefix} /></div>
        </Reveal>
      </div>
      <div className="wrap mt-12"><InfoStrip /></div>
      {showMap && (
        <div className="wrap mt-6">
          <div className="overflow-hidden rounded-[24px] ring-1 ring-cream/10" data-testid="contact-map">
            <iframe title={`Map of ${SITE.city}`} src={`https://www.google.com/maps?q=${encodeURIComponent(SITE.city)}&output=embed`} className="h-72 w-full border-0 grayscale-[30%]" loading="lazy" />
          </div>
        </div>
      )}
    </section>
  );
}
